import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Button,
  Modal,
  Form,
  FloatingLabel,
  Badge,
  Spinner,
  Alert,
  InputGroup,
  Nav,
  Dropdown,
  Offcanvas,
  ListGroup,
  ProgressBar,
} from 'react-bootstrap';
import { Megaphone, Plus, Search, Pencil, Trash2, EllipsisVertical, LayersPlus } from 'lucide-react';
import SuperAdminSidebar from '../components/SuperAdminSidebar';
import AdminTopBar from '../components/AdminTopBar';
import { API_ENDPOINTS } from '../config/api';

const TYPE_VARIANTS = {
  general: 'secondary',
  info: 'info',
  important: 'warning',
  urgent: 'danger',
};

const EMPTY_FORM = {
  title: '',
  content: '',
  startDate: '',
  endDate: '',
  type: 'general',
  audience: 'all',
  selectedProgramId: '',
  attachmentsText: '',
  requiresAcknowledgement: false,
};

const toDateOnly = (value) => (value ? String(value).slice(0, 10) : '');

const formatDateDisplay = (value) => {
  if (!value) return '—';
  const d = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const getAnnouncementStatus = (ann) => {
  const todayStr = new Date().toISOString().slice(0, 10);
  const start = toDateOnly(ann.startDate);
  const end = toDateOnly(ann.endDate);
  if (end && todayStr > end) return { label: 'Expired', variant: 'secondary' };
  if (start && todayStr < start) return { label: 'Scheduled', variant: 'info' };
  return { label: 'Active', variant: 'success' };
};

const getAudienceLabel = (ann) => {
  const audience = ann.audience || 'all';
  if (audience === 'program') {
    return ann.program?.name ? `${ann.program.name}` : 'Specific program';
  }
  if (audience === 'directors') return 'Program directors';
  if (audience === 'residents') return 'Residents';
  if (audience === 'attendings') return 'Attendings / faculty';
  return 'All users in institution';
};

const parseAttachments = (attachmentsText) => {
  if (!attachmentsText) return [];
  return attachmentsText
    .split(/\n|,/g)
    .map((item) => item.trim())
    .filter(Boolean);
};

const CircularMetric = ({ value = 0, label, variant = 'primary', countLabel }) => {
  const pct = Math.max(0, Math.min(100, Number(value) || 0));
  const radius = 26;
  const stroke = 6;
  const normalizedRadius = radius - stroke / 2;
  const circumference = 2 * Math.PI * normalizedRadius;
  const strokeDashoffset = circumference - (pct / 100) * circumference;

  const strokeColor = variant === 'warning'
    ? '#f59f00'
    : variant === 'success'
      ? '#2f9e44'
      : '#228be6';

  return (
    <div className="d-flex flex-column align-items-center justify-content-center p-2 rounded bg-body-tertiary h-100">
      <svg width={radius * 2} height={radius * 2} className="mb-1">
        <circle
          stroke="rgba(127,127,127,0.25)"
          fill="transparent"
          strokeWidth={stroke}
          r={normalizedRadius}
          cx={radius}
          cy={radius}
        />
        <circle
          stroke={strokeColor}
          fill="transparent"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${circumference} ${circumference}`}
          style={{ strokeDashoffset, transition: 'stroke-dashoffset 0.35s ease' }}
          r={normalizedRadius}
          cx={radius}
          cy={radius}
          transform={`rotate(-90 ${radius} ${radius})`}
        />
        <text
          x="50%"
          y="50%"
          dominantBaseline="middle"
          textAnchor="middle"
          style={{ fontSize: '0.7rem', fontWeight: 600 }}
        >
          {pct}%
        </text>
      </svg>
      <div className="small fw-semibold">{label}</div>
      <div className="small text-muted">{countLabel}</div>
    </div>
  );
};

const InstitutionalAnnouncementsPage = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [expandedMessages, setExpandedMessages] = useState({});
  const [programOptions, setProgramOptions] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [showDetailDrawer, setShowDetailDrawer] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [audiencePreview, setAudiencePreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const role = localStorage.getItem('role');
    if (!token || role !== 'institutional_admin') {
      window.location.replace('/');
    }
  }, []);

  const loadAnnouncements = useCallback(async () => {
    try {
      setError('');
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('You must be signed in to view announcements.');
      }
      const res = await fetch(`${API_ENDPOINTS.INSTITUTIONAL_ADMIN}/announcements`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to load announcements');
      }
      const data = await res.json();
      // Deduplicate by composite key – the server creates one row per program,
      // but from an institution perspective they are the same announcement.
      const seen = new Map();
      for (const ann of data) {
        const key = `${ann.title}|${ann.startDate}|${ann.endDate}|${ann.content}|${ann.type}|${ann.audience}`;
        if (!seen.has(key)) seen.set(key, ann);
      }
      const unique = Array.from(seen.values()).sort((a, b) => {
        const da = new Date(a.createdAt || 0).getTime();
        const db = new Date(b.createdAt || 0).getTime();
        return db - da;
      });
      setAnnouncements(unique);
    } catch (err) {
      setError(err.message || 'Failed to load announcements');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAnnouncements();
  }, [loadAnnouncements]);

  useEffect(() => {
    const loadPrograms = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await fetch(`${API_ENDPOINTS.INSTITUTIONAL_ADMIN}/programs`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        setProgramOptions(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Failed to load institutional programs for announcements', err);
      }
    };
    loadPrograms();
  }, []);

  const loadTemplates = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await fetch(`${API_ENDPOINTS.INSTITUTIONAL_ADMIN}/announcement-templates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      setTemplates(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load announcement templates', err);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  useEffect(() => {
    const loadPreview = async () => {
      if (!showModal) return;
      if (form.audience === 'program' && !form.selectedProgramId) {
        setAudiencePreview(null);
        return;
      }
      try {
        setPreviewLoading(true);
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await fetch(`${API_ENDPOINTS.INSTITUTIONAL_ADMIN}/announcements/audience-preview`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            audience: form.audience,
            selectedProgramId: form.selectedProgramId || undefined,
          }),
        });
        if (!res.ok) {
          setAudiencePreview(null);
          return;
        }
        const data = await res.json();
        setAudiencePreview(data);
      } catch (err) {
        setAudiencePreview(null);
      } finally {
        setPreviewLoading(false);
      }
    };
    loadPreview();
  }, [showModal, form.audience, form.selectedProgramId]);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setFormError('');
  };

  const openNew = () => {
    setEditingAnnouncement(null);
    resetForm();
    setShowModal(true);
  };

  const openEdit = (ann) => {
    setEditingAnnouncement(ann);
    setForm({
      title: ann.title || '',
      content: ann.content || '',
      startDate: toDateOnly(ann.startDate),
      endDate: toDateOnly(ann.endDate),
      type: ann.type || 'general',
      audience: ann.audience || 'all',
      selectedProgramId: ann.audience === 'program' ? String(ann.programId || '') : '',
      attachmentsText: Array.isArray(ann.attachments) ? ann.attachments.join('\n') : '',
      requiresAcknowledgement: !!ann.requiresAcknowledgement,
    });
    setFormError('');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingAnnouncement(null);
    resetForm();
  };

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 3000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    if (form.startDate && form.endDate && form.startDate > form.endDate) {
      setFormError('Start date cannot be after end date.');
      return;
    }
    if (form.audience === 'program' && !form.selectedProgramId) {
      setFormError('Please choose a program for program-specific audience.');
      return;
    }

    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('You must be signed in.');
      }

      const baseEndpoint = `${API_ENDPOINTS.INSTITUTIONAL_ADMIN}/announcements`;
      const isEditing = !!editingAnnouncement;
      const endpoint = isEditing ? `${baseEndpoint}/${editingAnnouncement.id}` : baseEndpoint;
      const method = isEditing ? 'PUT' : 'POST';

      const payload = {
        title: form.title,
        content: form.content,
        startDate: form.startDate,
        endDate: form.endDate,
        type: form.type || 'general',
        audience: form.audience || 'all',
        selectedProgramId: form.audience === 'program' ? form.selectedProgramId : undefined,
        attachments: parseAttachments(form.attachmentsText),
        requiresAcknowledgement: !!form.requiresAcknowledgement,
      };

      const res = await fetch(endpoint, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to save announcement');
      }

      await loadAnnouncements();
      closeModal();
      showSuccess(isEditing ? 'Announcement updated.' : 'Announcement sent.');
    } catch (err) {
      setFormError(err.message || 'Failed to save announcement');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!editingAnnouncement) return;
    if (!window.confirm('Delete this announcement? This action cannot be undone.')) return;

    setIsDeleting(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('You must be signed in.');
      }
      const res = await fetch(
        `${API_ENDPOINTS.INSTITUTIONAL_ADMIN}/announcements/${editingAnnouncement.id}`,
        { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to delete announcement');
      }
      await loadAnnouncements();
      closeModal();
      showSuccess('Announcement deleted.');
    } catch (err) {
      setFormError(err.message || 'Failed to delete announcement');
    } finally {
      setIsDeleting(false);
    }
  };

  const saveCurrentAsTemplate = async () => {
    if (!form.title || !form.content) {
      setFormError('Add a title and message before saving as template.');
      return;
    }
    const name = window.prompt('Template name');
    if (!name) return;
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await fetch(`${API_ENDPOINTS.INSTITUTIONAL_ADMIN}/announcement-templates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name,
          title: form.title,
          content: form.content,
          type: form.type || 'general',
          audience: form.audience || 'all',
          selectedProgramId: form.audience === 'program' ? form.selectedProgramId : undefined,
          attachments: parseAttachments(form.attachmentsText),
          requiresAcknowledgement: !!form.requiresAcknowledgement,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to save template');
      }
      await loadTemplates();
      showSuccess('Template saved.');
    } catch (err) {
      setFormError(err.message || 'Failed to save template');
    }
  };

  const applyTemplateToForm = (template) => {
    if (!template) return;
    setForm((prev) => ({
      ...prev,
      title: template.title || '',
      content: template.content || '',
      type: template.type || 'general',
      audience: template.audience || 'all',
      selectedProgramId: template.audience === 'program' ? String(template.selectedProgramId || '') : '',
      attachmentsText: Array.isArray(template.attachments) ? template.attachments.join('\n') : '',
      requiresAcknowledgement: !!template.requiresAcknowledgement,
    }));
  };

  const openDetails = async (announcement) => {
    try {
      setShowDetailDrawer(true);
      setDetailLoading(true);
      setDetailData(null);
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await fetch(`${API_ENDPOINTS.INSTITUTIONAL_ADMIN}/announcements/${announcement.id}/detail`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to load announcement details');
      }
      const data = await res.json();
      setDetailData(data);
    } catch (err) {
      setError(err.message || 'Failed to load announcement details');
      setShowDetailDrawer(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const filteredAnnouncements = useMemo(() => {
    const term = search.trim().toLowerCase();
    return announcements.filter((ann) => {
      const status = getAnnouncementStatus(ann).label.toLowerCase();
      if (statusFilter !== 'all' && status !== statusFilter) return false;
      if (typeFilter !== 'all' && (ann.type || 'general') !== typeFilter) return false;
      if (!term) return true;
      const haystack = `${ann.title || ''} ${ann.content || ''}`.toLowerCase();
      return haystack.includes(term);
    });
  }, [announcements, search, statusFilter, typeFilter]);

  const counts = useMemo(() => {
    const c = { total: announcements.length, active: 0, scheduled: 0, expired: 0 };
    for (const ann of announcements) {
      const label = getAnnouncementStatus(ann).label.toLowerCase();
      if (label === 'active') c.active += 1;
      else if (label === 'scheduled') c.scheduled += 1;
      else if (label === 'expired') c.expired += 1;
    }
    return c;
  }, [announcements]);

  const breadcrumbItems = [
    { href: '/institutional-admin/dashboard', label: 'Dashboard' },
    { label: 'Announcements' },
  ];

  const toggleExpandedMessage = (announcementId) => {
    setExpandedMessages((prev) => ({
      ...prev,
      [announcementId]: !prev[announcementId],
    }));
  };

  return (
    <div className="d-flex vh-100">
      <SuperAdminSidebar variant="institutional" />
      <div className="d-flex flex-column flex-grow-1 min-w-0 overflow-auto">
        <AdminTopBar variant="institutional" breadcrumbItems={breadcrumbItems} />
        <div className="flex-grow-1">
          <Container fluid className="bg-inverse-light">
            <Row className="g-3">
              <Col xl={2} lg={3} md={3} sm={4} className="border-end bg-inverse-light pt-3">
                <div className="d-flex flex-column mb-3 px-2">
                  
                    <h6 className="mb-0 d-flex align-items-center gap-2">
                      Announcements
                    </h6>                  
                </div>

                <Nav
                  variant="secondary-tabs"
                  className="flex-column"
                  activeKey={statusFilter}
                  onSelect={(k) => setStatusFilter(k || 'all')}
                >
                  <Nav.Item>
                    <Nav.Link eventKey="all" className="d-flex align-items-center justify-content-between">
                      All Announcements
                      <Badge bg="secondary" className="ms-2">{counts.total}</Badge>
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="active" className="d-flex align-items-center justify-content-between">
                      Active
                      <Badge bg="secondary" className="ms-2">{counts.active}</Badge>
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="scheduled" className="d-flex align-items-center justify-content-between">
                      Scheduled
                      <Badge bg="secondary" className="ms-2">{counts.scheduled}</Badge>
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="expired" className="d-flex align-items-center justify-content-between">
                      Expired
                      <Badge bg="secondary" className="ms-2">{counts.expired}</Badge>
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
              <Alert variant="info" className="my-3">
                <div className="small">
                  <strong>Hint:</strong> <br /> Here you can view, filter, and manage all institution-wide announcements. 
                  Use the tabs above to browse by status, create new announcements, or select an existing one to edit or view analytics. 
                  Templates help you quickly draft common messages. click an announcement to view its details and analytics.
                </div>
              </Alert>
         
              </Col>

              <Col xl={10} lg={9} md={9} sm={8} className="p-0 d-block adjust-min-vh">
                {error && (
                  <Alert variant="danger" className="m-3 mb-0" onClose={() => setError('')} dismissible>
                    {error}
                  </Alert>
                )}
                {successMsg && (
                  <Alert variant="success" className="m-3 mb-0" onClose={() => setSuccessMsg('')} dismissible>
                    {successMsg}
                  </Alert>
                )}

                <Card className="p-0 border-0 rounded-0 h-100">
                  <Card.Header className="d-flex justify-content-between align-items-center gap-3 px-4 py-3 custom-sticky-top border-bottom-0">
                    <div className="d-flex flex-column">
                      <h6 className="mb-0 text-capitalize">{statusFilter} Announcements</h6>
                      <div className="text-muted small">
                        Manage institution-wide announcements and publication windows.
                      </div>
                    </div>
                    <div className="d-flex flex-row align-items-center gap-2">
                  <InputGroup size="sm">
                    <InputGroup.Text>
                      <Search size={14} />
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="Search..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </InputGroup>
                  <Form.Select
                    size="sm"
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                  >
                    <option value="all">All types</option>
                    <option value="general">General</option>
                    <option value="info">Info</option>
                    <option value="important">Important</option>
                    <option value="urgent">Urgent</option>
                  </Form.Select>
                  <Dropdown align="end">
                    <Dropdown.Toggle variant="light-lite" size="sm" className="text-nowrap no-arrow">
                      <LayersPlus size={14} className="me-1" />
                      From Template
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      {templates.length === 0 ? (
                        <Dropdown.Item disabled>No templates yet</Dropdown.Item>
                      ) : (
                        templates.map((tpl) => (
                          <Dropdown.Item
                            key={tpl.id}
                            onClick={() => {
                              openNew();
                              setTimeout(() => applyTemplateToForm(tpl), 0);
                            }}
                          >
                            {tpl.name}
                          </Dropdown.Item>
                        ))
                      )}
                    </Dropdown.Menu>
                  </Dropdown>
                
                    <Button variant="primary" size="sm" className="text-nowrap" onClick={openNew}>
                      New Announcement
                    </Button></div>
                  </Card.Header>
                  <Card.Body className="p-0">
                    {loading ? (
                      <div className="d-flex justify-content-center py-5">
                        <Spinner animation="border" />
                      </div>
                    ) : filteredAnnouncements.length === 0 ? (
                      <div className="text-center py-5 text-muted">
                        {announcements.length === 0
                          ? 'No announcements yet. Click "New Announcement" to create one.'
                          : 'No announcements match your filters.'}
                      </div>
                    ) : (
                      <div className="table-responsive alt-table-striped programs-table-scroll-wrapper">
                        <Table hover className="table-header-sticky mb-0 align-middle">
                          <thead className="table-light">
                            <tr>
                              <th>Title</th>
                              <th>Audience</th>
                              <th>Type</th>
                              <th>Status</th>
                              <th>Start</th>
                              <th>End</th>
                              <th>
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredAnnouncements.map((ann) => {
                              const status = getAnnouncementStatus(ann);
                              const typeVariant = TYPE_VARIANTS[ann.type] || 'secondary';
                              return (
                                <tr key={ann.id} style={{ cursor: 'pointer' }} onClick={() => openDetails(ann)}>
                                  <td style={{ minWidth: '163px', width: 'fit-content' }}>
                                    <div className="fw-semibold">{ann.title}</div>
                                    <div className="small text-muted text-wrap">
                                      {(() => {
                                        const message = ann.content || '';
                                        const isExpanded = !!expandedMessages[ann.id];
                                        const isLong = message.length > 100;
                                        const visibleText = isExpanded || !isLong
                                          ? message
                                          : `${message.slice(0, 100)}...`;

                                        return (
                                          <>
                                            <span>{visibleText}</span>
                                            {isLong && (
                                              <Button
                                                variant="link"
                                                size="sm"
                                                className="p-0 ms-1 align-baseline text-decoration-none"
                                                style={{ fontSize: '0.75rem' }}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  toggleExpandedMessage(ann.id);
                                                }}
                                              >
                                                {isExpanded ? 'Show less' : 'Show more'}
                                              </Button>
                                            )}
                                          </>
                                        );
                                      })()}
                                    </div>
                                  </td>
                                  <td>
                                    <span>{getAudienceLabel(ann)}</span>
                                  </td>
                                  <td>
                                    <Badge bg={typeVariant} className="text-capitalize">
                                      {ann.type || 'general'}
                                    </Badge>
                                  </td>
                                  <td>
                                    <Badge bg={status.variant}>{status.label}</Badge>
                                  </td>
                                  <td>{formatDateDisplay(ann.startDate)}</td>
                                  <td>{formatDateDisplay(ann.endDate)}</td>
                                  <td onClick={(e) => e.stopPropagation()}>
                                  
                                    <Dropdown align="end">
                                      <Dropdown.Toggle
                                        variant="light-lite"
                                        size="sm"
                                        className="no-arrow"
                                        id={`dropdown-announcement-actions-${ann.id}`}
                                        aria-label="Announcement actions"
                                      >
                                        <EllipsisVertical />
                                      </Dropdown.Toggle>
                                      <Dropdown.Menu>
                                        <Dropdown.Item
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            openEdit(ann);
                                          }}
                                        >
                                          <Pencil  className="me-2" />
                                          Edit
                                        </Dropdown.Item>
                                        <Dropdown.Item
                                          onClick={async (e) => {
                                            e.stopPropagation();
                                            setEditingAnnouncement(ann);
                                            setTimeout(async () => {
                                              if (!window.confirm('Delete this announcement? This action cannot be undone.')) {
                                                setEditingAnnouncement(null);
                                                return;
                                              }
                                              try {
                                                const token = localStorage.getItem('token');
                                                const res = await fetch(
                                                  `${API_ENDPOINTS.INSTITUTIONAL_ADMIN}/announcements/${ann.id}`,
                                                  { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
                                                );
                                                if (!res.ok) {
                                                  const data = await res.json().catch(() => ({}));
                                                  throw new Error(data.message || 'Failed to delete');
                                                }
                                                await loadAnnouncements();
                                                showSuccess('Announcement deleted.');
                                              } catch (err) {
                                                setError(err.message || 'Failed to delete');
                                              } finally {
                                                setEditingAnnouncement(null);
                                              }
                                            }, 0);
                                          }}
                                          className="text-danger"
                                        >
                                          <Trash2 className="me-2" />
                                          Delete
                                        </Dropdown.Item>
                                      </Dropdown.Menu>
                                    </Dropdown>
                               
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Container>
        </div>
      </div>

      <Modal show={showModal} onHide={closeModal} centered>
        <Modal.Header closeButton className="border-bottom-0 px-4">
          <Modal.Title className="w-100">
            <span>{editingAnnouncement ? 'Edit Announcement' : 'Create Announcement'}</span>
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body className="px-4">
            {formError && <div className="alert alert-danger py-2 small">{formError}</div>}
            <Form.Group className="mb-3">
              <FloatingLabel label="Title" className="required-field">
                <Form.Control
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  required
                  placeholder="Enter announcement title"
                />
              </FloatingLabel>
            </Form.Group>
            <div className="d-flex gap-2">
              <Form.Group className="mb-3 flex-grow-1">
                <FloatingLabel label="Audience" className="required-field">
                  <Form.Select
                    value={form.audience}
                    onChange={(e) => setForm({ ...form, audience: e.target.value, selectedProgramId: '' })}
                  >
                    <option value="all">All users in this institution</option>
                    <option value="directors">Program directors</option>
                    <option value="residents">Residents</option>
                    <option value="attendings">Attendings / faculty</option>
                    <option value="program">Specific program</option>
                  </Form.Select>
                </FloatingLabel>
              </Form.Group>
              <Form.Group className="mb-3">
                <FloatingLabel label="Importance" className="required-field">
                  <Form.Select
                    value={form.type}
                    onChange={(e) => setForm({ ...form, type: e.target.value })}
                  >
                    <option value="general">General</option>
                    <option value="important">Important</option>
                    <option value="urgent">Urgent</option>
                    <option value="info">Info</option>
                  </Form.Select>
                </FloatingLabel>
              </Form.Group>
            </div>
            {form.audience === 'program' && (
              <Form.Group className="mb-3">
                <FloatingLabel label="Program" className="required-field">
                  <Form.Select
                    value={form.selectedProgramId || ''}
                    onChange={(e) => setForm({ ...form, selectedProgramId: e.target.value })}
                    required
                  >
                    <option value="">Select a program</option>
                    {programOptions.map((program) => (
                      <option key={program.id} value={program.id}>
                        {program.name}
                      </option>
                    ))}
                  </Form.Select>
                </FloatingLabel>
              </Form.Group>
            )}
            <div className="mb-3">
              <div className="small text-muted">
                {previewLoading ? 'Calculating audience...' : audiencePreview ? (
                  `This announcement will notify ${audiencePreview.total} users (${audiencePreview.byRole.directors} directors, ${audiencePreview.byRole.residents} residents, ${audiencePreview.byRole.attendings} attendings).`
                ) : 'Audience preview unavailable.'}
              </div>
            </div>
            <div className="d-flex gap-2">
              <Form.Group className="mb-3 flex-grow-1">
                <FloatingLabel label="Start date" className="required-field">
                  <Form.Control
                    type="date"
                    value={form.startDate}
                    onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                    required
                  />
                </FloatingLabel>
              </Form.Group>
              <Form.Group className="mb-3 flex-grow-1">
                <FloatingLabel label="End date" className="required-field">
                  <Form.Control
                    type="date"
                    value={form.endDate}
                    onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                    required
                  />
                </FloatingLabel>
              </Form.Group>
            </div>
            <Form.Group className="mb-2">
              <FloatingLabel label="Message" className="required-field">
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={form.content}
                  onChange={(e) => setForm({ ...form, content: e.target.value })}
                  required
                  placeholder="Write your announcement message"
                  style={{ minHeight: '120px' }}
                />
              </FloatingLabel>
            </Form.Group>
            <Form.Group className="mb-3 mt-3">
              <FloatingLabel label="Attachment links (one per line)">
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={form.attachmentsText}
                  onChange={(e) => setForm({ ...form, attachmentsText: e.target.value })}
                  placeholder="https://example.com/policy.pdf"
                  style={{ minHeight: '100px' }}
                />
              </FloatingLabel>
            </Form.Group>
            <Form.Check
              type="switch"
              id="announcement-requires-ack"
              className="mb-2"
              label="Require acknowledgement (I acknowledge)"
              checked={!!form.requiresAcknowledgement}
              onChange={(e) => setForm({ ...form, requiresAcknowledgement: e.target.checked })}
            />
          </Modal.Body>
          <Modal.Footer className="px-4 border-top-0">
            <Button
              variant="outline-secondary"
              size="sm"
              type="button"
              onClick={saveCurrentAsTemplate}
              disabled={isSubmitting || isDeleting}
            >
              Save as Template
            </Button>
            <Button variant="light" size="sm" onClick={closeModal} disabled={isSubmitting || isDeleting}>
              Cancel
            </Button>
            {editingAnnouncement && (
              <Button
                variant="outline-danger"
                size="sm"
                type="button"
                onClick={handleDelete}
                disabled={isSubmitting || isDeleting}
              >
                {isDeleting ? 'Deleting…' : 'Delete'}
              </Button>
            )}
            <Button variant="primary" size="sm" type="submit" disabled={isSubmitting || isDeleting}>
              {isSubmitting
                ? 'Saving…'
                : editingAnnouncement
                  ? 'Update Announcement'
                  : 'Send Announcement'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
      <Offcanvas show={showDetailDrawer} onHide={() => setShowDetailDrawer(false)} placement="end" style={{ width: 520 }}>
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>Announcement Details</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          {detailLoading ? (
            <div className="d-flex justify-content-center py-5"><Spinner animation="border" /></div>
          ) : !detailData ? (
            <div className="text-muted">No details available.</div>
          ) : (
            <>
              {(() => {
                const announcement = detailData.announcement || {};
                const analytics = detailData.analytics || {};
                const requiresAck = !!announcement.requiresAcknowledgement;
                return (
                  <>
              <h6 className=" mb-0 fw-semibold">{detailData.announcement?.title}</h6>
              <div className="text-muted small mb-2">
                {formatDateDisplay(detailData.announcement?.startDate)} - {formatDateDisplay(detailData.announcement?.endDate)}
              </div>
              <p className="mb-3 small">{detailData.announcement?.content}</p>
              {Array.isArray(detailData.announcement?.attachments) && detailData.announcement.attachments.length > 0 && (
                <Card className="mb-3">
                  <Card.Header className="py-2">Attachments</Card.Header>
                  <ListGroup variant="flush">
                    {detailData.announcement.attachments.map((link, idx) => (
                      <ListGroup.Item key={`detail-att-${idx}`} className="py-2">
                        <a href={link} target="_blank" rel="noopener noreferrer">{link}</a>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </Card>
              )}
              <Card className="mb-3 border-0">
                <Card.Header className="py-3 p-0">
                  <div className="d-flex align-items-center justify-content-between">
                    <span className="fw-semibold">Read receipts / analytics</span>
                    <Badge bg="light" text="dark">{analytics.targetCount || 0} targeted</Badge>
                  </div>
                </Card.Header>
                <Card.Body className="pt-3 p-0">
                  <Row className="g-2">
                    <Col xs={6}>
                      <CircularMetric
                        value={analytics.viewedPct || 0}
                        label="Viewed"
                        variant="primary"
                        countLabel={`${analytics.viewedCount || 0}/${analytics.targetCount || 0}`}
                      />
                    </Col>
                    <Col xs={6}>
                      <CircularMetric
                        value={analytics.dismissedPct || 0}
                        label="Dismissed"
                        variant="warning"
                        countLabel={`${analytics.dismissedCount || 0}/${analytics.targetCount || 0}`}
                      />
                    </Col>
                    {requiresAck && (
                      <Col xs={12}>
                        <CircularMetric
                          value={analytics.acknowledgedPct || 0}
                          label="Acknowledged"
                          variant="success"
                          countLabel={`${analytics.acknowledgedCount || 0}/${analytics.targetCount || 0}`}
                        />
                      </Col>
                    )}
                  </Row>            
                </Card.Body>
              </Card>
              <Card className="mb-3 border-0">
                <Card.Header className="py-3 p-0 d-flex justify-content-between align-items-center">
                  <span className="fw-semibold">By Role</span>
                  <Badge bg="light" text="dark">Segmented performance</Badge>
                </Card.Header>
                <Card.Body className="p-0">
                  <div className="table-responsive ">
                    <Table size="sm" className="mb-0 align-middle custom-modern-responsive announcement-details-tables">
                      <thead>
                        <tr className="border-bottom">
                          <th>Role</th>
                          <th>Users</th>
                          <th style={{ minWidth: 140 }}>Viewed (%)</th>
                          <th>Dismissed</th>
                          {requiresAck && <th>Acknowledged</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {(detailData.analytics?.byRole || []).map((row) => (
                          <tr key={`role-${row.role}`}>
                            <td className="text-capitalize fw-semibold">{row.role}</td>
                            <td>{row.target}</td>
                            <td>
                              <div className="small text-muted mb-1">{row.viewedPct}% ({row.viewed}/{row.target})</div>
                              <ProgressBar now={row.viewedPct} style={{ height: 6 }} />
                            </td>
                            <td>
                              <Badge bg="warning-subtle" text="dark">{row.dismissedPct}% ({row.dismissed}/{row.target})</Badge>
                            </td>
                            {requiresAck && (
                              <td>
                                <Badge bg="success-subtle" text="dark">{row.acknowledgedPct}% ({row.acknowledged}/{row.target})</Badge>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
              <Card className="border-0">
                <Card.Header className="py-3 p-0 d-flex justify-content-between align-items-center">
                  <span className="fw-semibold">By Program</span>
                  <Badge bg="light" text="dark">Program-level performance</Badge>
                </Card.Header>
                <Card.Body className="p-0">
                  <div className="table-responsive">
                    <Table size="sm" className="mb-0 align-middle custom-modern-responsive announcement-details-tables">
                      <thead>
                        <tr className="border-bottom">
                          <th>Program</th>
                          <th>Users</th>
                          <th style={{ minWidth: 140 }}>Viewed (%)</th>
                          <th>Dismissed</th>
                          {requiresAck && <th>Acknowledged</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {(detailData.analytics?.byProgram || []).map((row) => (
                          <tr key={`program-${row.programId}`}>
                            <td className="fw-semibold">{row.programName}</td>
                            <td>{row.target}</td>
                            <td>
                              <div className="small text-muted mb-1">{row.viewedPct}% ({row.viewed}/{row.target})</div>
                              <ProgressBar now={row.viewedPct} style={{ height: 6 }} />
                            </td>
                            <td>
                              <Badge bg="warning-subtle" text="dark">{row.dismissedPct}% ({row.dismissed}/{row.target})</Badge>
                            </td>
                            {requiresAck && (
                              <td>
                                <Badge bg="success-subtle" text="dark">{row.acknowledgedPct}% ({row.acknowledged}/{row.target})</Badge>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
                  </>
                );
              })()}
            </>
          )}
        </Offcanvas.Body>
      </Offcanvas>
    </div>
  );
};

export default InstitutionalAnnouncementsPage;
